import { useCallback, useEffect, useMemo, useRef } from 'react';
// @ts-ignore
import _ from 'lodash';

/**
 * useDebouncedAutoSave
 * --------------------
 * Author: Konstantina K.
 *
 * A custom React hook that wraps a save action with trailing-edge debouncing,
 * payload deduplication, and concurrency gating, so that bursts of user
 * interactions collapse into the minimum number of network calls.
 *
 * Behavior:
 * - Trailing-edge debounce (default 2000ms, maxWait 8000ms) - a burst of calls
 *   collapses into one save fired after the user pauses.
 * - Payload dedup via JSON.stringify - identical consecutive payloads are skipped.
 * - Concurrency gating - while a previous save is in flight, the latest payload
 *   is queued and fired automatically when the in-flight request completes.
 *   Prevents overlapping requests and dropped saves on slow networks.
 * - Unmount cancellation - any pending debounced save is cancelled on unmount.
 * - flushAutoSave() forces the pending debounced save out immediately, e.g. on
 *   form navigation or submit. If a save is in flight, the flushed payload is
 *   queued and fired when the in-flight request completes.
 *
 * `buildPayload` may return null to skip the save (e.g. when guards fail,
 * values aren't ready, or no meaningful change occurred). The latest closures
 * of `buildPayload` and `send` are always used, so they can read fresh state
 * without resetting the debounce timer.
 *
 * Usage:
 * const { onAutoSave, flushAutoSave } = useDebouncedAutoSave({
 *   send: (payload) => dispatch(saveAction(payload)),
 *   buildPayload: (values, errors) => {
 *     if (!enabled || errors?.error) return null;
 *     return { ...buildDto(values) };
 *   },
 *   isInFlight: !isRequestFinished,
 * });
 */

type UseDebouncedAutoSaveProps<TValues, TMeta, TPayload> = {
    send: (payload: TPayload) => void;
    buildPayload: (
        values: TValues,
        errors: TMeta,
    ) => TPayload | null | undefined;
    debounceMs?: number;
    maxWait?: number;
    isInFlight?: boolean;
};

type UseDebouncedAutoSaveReturn<TValues, TMeta> = {
    onAutoSave: (values: TValues, errors: TMeta) => void;
    flushAutoSave: () => void;
};

const useDebouncedAutoSave = <TValues, TMeta, TPayload>(
    props: UseDebouncedAutoSaveProps<TValues, TMeta, TPayload>,
): UseDebouncedAutoSaveReturn<TValues, TMeta> => {
    const {
        send,
        buildPayload,
        debounceMs = 2000,
        maxWait = 8000,
        isInFlight = false,
    } = props;

    const sendRef = useRef(send);
    const buildPayloadRef = useRef(buildPayload);
    const lastSerializedRef = useRef<string | null>(null);
    const isInFlightRef = useRef(isInFlight);
    const pendingPayloadRef = useRef<TPayload | null>(null);

    useEffect(() => {
        sendRef.current = send;
    }, [send]);
    useEffect(() => {
        buildPayloadRef.current = buildPayload;
    }, [buildPayload]);
    useEffect(() => {
        isInFlightRef.current = isInFlight;
    }, [isInFlight]);

    // Concurrency-gated dispatch: while a save is in flight, hold the latest
    // payload; the in-flight watcher below fires it when the request completes.
    const dispatchOrQueue = useCallback((payload: TPayload) => {
        if (isInFlightRef.current) {
            pendingPayloadRef.current = payload;
            return;
        }
        sendRef.current(payload);
    }, []);

    const debouncedSend = useMemo(
        () => _.debounce(dispatchOrQueue, debounceMs, { maxWait }),
        [debounceMs, maxWait, dispatchOrQueue],
    );

    useEffect(
        () => () => {
            debouncedSend.cancel();
        },
        [debouncedSend],
    );

    // When an in-flight save completes, fire any payload that was queued during it.
    useEffect(() => {
        if (!isInFlight && pendingPayloadRef.current) {
            const payload = pendingPayloadRef.current;
            pendingPayloadRef.current = null;
            sendRef.current(payload);
        }
    }, [isInFlight]);

    const onAutoSave = useCallback(
        (values: TValues, errors: TMeta) => {
            const payload = buildPayloadRef.current(values, errors);
            if (payload === null || payload === undefined) {
                return;
            }

            const serialized = JSON.stringify(payload);
            if (serialized === lastSerializedRef.current) {
                return;
            }
            lastSerializedRef.current = serialized;

            debouncedSend(payload);
        },
        [debouncedSend],
    );

    const flushAutoSave = useCallback(() => {
        debouncedSend.flush();
    }, [debouncedSend]);

    return { onAutoSave, flushAutoSave };
};

export default useDebouncedAutoSave;
